import { ArrowDownLeft, CircleCheckBig, Clock3, Landmark, LoaderCircle, Search, Wallet, XCircle } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import PaymentSplitInfo from './PaymentSplitInfo';
import {
  paymentService,
  type SplitTransferDetails,
  type SplitTransferEntry,
  type SplitTransferResult,
} from '../../../shared/services/paymentService';

type TransferRole = 'admin' | 'vendor' | 'buyer';
type TransferStatus = 'completed' | 'processing' | 'pending' | 'failed';

interface TransferProcessBoardProps {
  role: TransferRole;
}

const formatCurrency = (amount: number, currency: string): string => {
  try {
    return new Intl.NumberFormat('en-RW', { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(0)}`;
  }
};

const formatDate = (value: string): string =>
  new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));

const normalizeTransferStatus = (value: string): TransferStatus => {
  const normalized = value.trim().toLowerCase();
  if (['completed', 'complete', 'successful', 'success', 'paid', 'claimed'].includes(normalized)) {
    return 'completed';
  }
  if (['processing', 'in_progress', 'in-progress'].includes(normalized)) {
    return 'processing';
  }
  if (['failed', 'cancelled', 'canceled', 'declined', 'error'].includes(normalized)) {
    return 'failed';
  }
  return 'pending';
};

const statusLabel: Record<TransferStatus, string> = {
  completed: 'Completed',
  processing: 'Processing',
  pending: 'Pending',
  failed: 'Failed',
};

const statusTone: Record<TransferStatus, string> = {
  completed: 'bg-emerald-100 text-emerald-700',
  processing: 'bg-orange-100 text-orange-700',
  pending: 'bg-amber-100 text-amber-700',
  failed: 'bg-rose-100 text-rose-700',
};

const getRoleCopy = (role: TransferRole) => {
  if (role === 'admin') {
    return {
      title: 'Platform Split Transfers',
      subtitle: 'Review every 30% platform share, 70% creator share, and payout progress from paid orders.',
      emptyTitle: 'No platform split transfers yet.',
      emptyBody: 'Paid orders will appear here with the 30/70 split breakdown for admin review.',
      primaryMetricLabel: 'Platform 30%',
      primaryMetricTone: 'text-orange-700',
      secondaryMetricLabel: 'Creator 70%',
      secondaryMetricTone: 'text-emerald-700',
    };
  }

  return {
    title: 'Your Published Content Earnings',
    subtitle: 'Track the 70% you earn from published content and follow each payout from sale to completion.',
    emptyTitle: 'No creator transfers yet.',
    emptyBody: 'Once your uploaded content starts selling on the website, your 70% earnings will appear here.',
    primaryMetricLabel: 'Your 70%',
    primaryMetricTone: 'text-emerald-700',
    secondaryMetricLabel: 'Platform 30%',
    secondaryMetricTone: 'text-orange-700',
  };
};

const summarizeNote = (entry: SplitTransferEntry): string => {
  const trimmed = entry.note.trim();
  if (trimmed) {
    return trimmed;
  }

  if (entry.itemCount > 0) {
    return `${entry.itemCount} item${entry.itemCount === 1 ? '' : 's'} in this transfer`;
  }

  return 'Split transfer record';
};

const TransferProcessBoard = ({ role }: TransferProcessBoardProps) => {
  const [transfers, setTransfers] = useState<SplitTransferEntry[]>([]);
  const [stats, setStats] = useState<SplitTransferResult['stats']>();
  const [selectedStatuses, setSelectedStatuses] = useState<TransferStatus[]>(['completed', 'processing', 'pending', 'failed']);
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedTransferId, setSelectedTransferId] = useState<string | null>(null);
  const [selectedTransferDetails, setSelectedTransferDetails] = useState<SplitTransferDetails | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState('');

  const roleCopy = getRoleCopy(role);
  const isAdmin = role === 'admin';

  const fetchTransfers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const result =
        isAdmin
          ? await paymentService.getAdminSplitTransfers()
          : await paymentService.getCreatorSplitTransfers();

      if (result.success) {
        setTransfers(result.data);
        setStats(result.stats);
      } else {
        setTransfers([]);
        setStats(undefined);
        setError('Unable to load transfer records.');
      }
    } catch (fetchError: unknown) {
      setTransfers([]);
      setStats(undefined);
      setError(fetchError instanceof Error ? fetchError.message : 'Unable to load transfer records.');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    void fetchTransfers();
  }, [fetchTransfers]);

  const filteredTransfers = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();
    return transfers.filter((transfer) => {
      const currentStatus = normalizeTransferStatus(transfer.transferStatus);
      const statusMatch = selectedStatuses.includes(currentStatus);
      if (!statusMatch) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const haystack = [
        transfer.transactionRef,
        transfer.orderId,
        transfer.note,
        transfer.customer?.name,
        transfer.customer?.email,
        transfer.seller?.name,
        transfer.seller?.email,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(normalizedSearch);
    });
  }, [searchValue, selectedStatuses, transfers]);

  const selectedTransferSummary = useMemo(
    () => filteredTransfers.find((transfer) => transfer.id === selectedTransferId) ?? filteredTransfers[0] ?? null,
    [filteredTransfers, selectedTransferId],
  );

  useEffect(() => {
    if (!filteredTransfers.length) {
      if (selectedTransferId !== null) {
        setSelectedTransferId(null);
      }
      return;
    }

    const stillVisible = filteredTransfers.some((transfer) => transfer.id === selectedTransferId);
    if (!stillVisible) {
      setSelectedTransferId(filteredTransfers[0].id);
    }
  }, [filteredTransfers, selectedTransferId]);

  useEffect(() => {
    const transferId = selectedTransferSummary?.id;
    if (!transferId) {
      setSelectedTransferDetails(null);
      setDetailsError('');
      return;
    }

    let cancelled = false;

    const loadDetails = async () => {
      try {
        setDetailsLoading(true);
        setDetailsError('');
        const details = await paymentService.getSplitTransferDetails(transferId);
        if (!cancelled) {
          setSelectedTransferDetails(details);
        }
      } catch (detailsFetchError: unknown) {
        if (!cancelled) {
          setSelectedTransferDetails(null);
          setDetailsError(detailsFetchError instanceof Error ? detailsFetchError.message : 'Unable to load transfer details.');
        }
      } finally {
        if (!cancelled) {
          setDetailsLoading(false);
        }
      }
    };

    void loadDetails();

    return () => {
      cancelled = true;
    };
  }, [selectedTransferSummary?.id]);

  const metrics = useMemo(() => {
    if (stats) {
      return {
        primaryAmount: isAdmin ? stats.platformRevenue : stats.creatorPayouts,
        secondaryAmount: isAdmin ? stats.creatorPayouts : stats.platformRevenue,
        grossAmount: stats.grossAmount,
        completedCount: stats.completed,
        paidOutAmount: stats.paidOutAmount,
      };
    }

    const completedTransfers = filteredTransfers.filter(
      (transfer) => normalizeTransferStatus(transfer.transferStatus) === 'completed',
    );

    return {
      primaryAmount: filteredTransfers.reduce(
        (sum, transfer) => sum + (isAdmin ? transfer.platformCommission : transfer.creatorEarnings),
        0,
      ),
      secondaryAmount: filteredTransfers.reduce(
        (sum, transfer) => sum + (isAdmin ? transfer.creatorEarnings : transfer.platformCommission),
        0,
      ),
      grossAmount: filteredTransfers.reduce((sum, transfer) => sum + transfer.amount, 0),
      completedCount: completedTransfers.length,
      paidOutAmount: completedTransfers.reduce((sum, transfer) => sum + transfer.creatorEarnings, 0),
    };
  }, [filteredTransfers, isAdmin, stats]);

  const selectedTransfer = selectedTransferDetails ?? selectedTransferSummary;
  const selectedStatus = selectedTransfer ? normalizeTransferStatus(selectedTransfer.transferStatus) : null;

  return (
    <div className="space-y-4">
      <article className="rounded-3xl bg-gradient-to-r from-orange-500 to-orange-600 p-5 text-white shadow-sm sm:p-6">
        <div className="grid gap-3 xl:grid-cols-[minmax(0,1.4fr)_220px_200px]">
          <div>
            <h3 className="text-lg font-semibold">{roleCopy.title}</h3>
            <p className="mt-1 text-sm text-orange-100">{roleCopy.subtitle}</p>
          </div>
          <div className="rounded-2xl bg-white/15 px-3 py-3">
            <p className="text-xs uppercase tracking-[0.18em] text-orange-100">Updated</p>
            <p className="mt-1 text-sm font-semibold">
              {new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date())}
            </p>
          </div>
          <div className="rounded-2xl bg-white/15 px-3 py-3">
            <p className="text-xs uppercase tracking-[0.18em] text-orange-100">View</p>
            <p className="mt-1 text-sm font-semibold uppercase">{isAdmin ? 'Admin 30%' : 'Creator 70%'}</p>
          </div>
        </div>
      </article>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-4">
          <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <article className="rounded-2xl border border-orange-100 bg-white p-4">
              <p className="text-xs text-slate-500">{roleCopy.primaryMetricLabel}</p>
              <p className={`mt-2 text-2xl font-semibold ${roleCopy.primaryMetricTone}`}>
                {formatCurrency(metrics.primaryAmount, filteredTransfers[0]?.currency || 'RWF')}
              </p>
            </article>
            <article className="rounded-2xl border border-orange-100 bg-white p-4">
              <p className="text-xs text-slate-500">{roleCopy.secondaryMetricLabel}</p>
              <p className={`mt-2 text-2xl font-semibold ${roleCopy.secondaryMetricTone}`}>
                {formatCurrency(metrics.secondaryAmount, filteredTransfers[0]?.currency || 'RWF')}
              </p>
            </article>
            <article className="rounded-2xl border border-orange-100 bg-white p-4">
              <p className="text-xs text-slate-500">Gross Sales</p>
              <p className="mt-2 text-2xl font-semibold text-slate-900">
                {formatCurrency(metrics.grossAmount, filteredTransfers[0]?.currency || 'RWF')}
              </p>
            </article>
            <article className="rounded-2xl border border-orange-100 bg-white p-4">
              <p className="text-xs text-slate-500">{isAdmin ? 'Completed Orders' : 'Paid Out 70%'}</p>
              <p className="mt-2 text-2xl font-semibold text-slate-900">
                {isAdmin
                  ? metrics.completedCount
                  : formatCurrency(metrics.paidOutAmount, filteredTransfers[0]?.currency || 'RWF')}
              </p>
            </article>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h4 className="text-sm font-semibold text-slate-900">Transfer Activity</h4>
                <p className="mt-1 text-xs text-slate-500">
                  {isAdmin
                    ? 'Every paid order shows what the platform received and what the creator earned.'
                    : 'Every sale shows the amount that belongs to you and the current payout stage.'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => void fetchTransfers()}
                className="rounded-xl border border-orange-200 bg-orange-50 px-3 py-2 text-xs font-semibold text-orange-700 transition hover:bg-orange-100"
              >
                Refresh
              </button>
            </div>

            <div className="mt-4 grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto]">
              <label className="relative block">
                <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <input
                  value={searchValue}
                  onChange={(event) => setSearchValue(event.target.value)}
                  placeholder={isAdmin ? 'Search buyer, creator, or reference' : 'Search buyer or reference'}
                  className="w-full rounded-2xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm text-slate-800 focus:border-orange-400 focus:outline-none"
                />
              </label>

              <div className="flex flex-wrap gap-2">
                {(['completed', 'processing', 'pending', 'failed'] as const).map((status) => {
                  const active = selectedStatuses.includes(status);
                  return (
                    <button
                      key={status}
                      type="button"
                      onClick={() =>
                        setSelectedStatuses((current) => {
                          if (active) {
                            const next = current.filter((entry) => entry !== status);
                            return next.length === 0 ? current : next;
                          }
                          return [...current, status];
                        })
                      }
                      className={`rounded-full border px-3 py-2 text-xs font-semibold transition ${
                        active
                          ? 'border-orange-500 bg-orange-500 text-white'
                          : 'border-slate-200 bg-white text-slate-700 hover:border-orange-200 hover:bg-orange-50'
                      }`}
                    >
                      {statusLabel[status]}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-4 space-y-3">
              {loading ? (
                <article className="flex items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 px-4 py-14 text-sm text-slate-500">
                  <LoaderCircle className="mr-2 h-5 w-5 animate-spin" />
                  Loading transfer records...
                </article>
              ) : error ? (
                <article className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-4 text-sm text-rose-700">{error}</article>
              ) : filteredTransfers.length === 0 ? (
                <article className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-5 py-12 text-center">
                  <Landmark className="mx-auto h-10 w-10 text-slate-300" />
                  <p className="mt-4 text-sm font-semibold text-slate-700">{roleCopy.emptyTitle}</p>
                  <p className="mt-1 text-sm text-slate-500">{roleCopy.emptyBody}</p>
                </article>
              ) : (
                filteredTransfers.map((transfer) => {
                  const currentStatus = normalizeTransferStatus(transfer.transferStatus);
                  const isSelected = selectedTransferSummary?.id === transfer.id;
                  const leadingName = isAdmin
                    ? transfer.customer?.name || 'Customer'
                    : transfer.customer?.name || 'Website buyer';
                  const secondaryName = isAdmin
                    ? transfer.seller?.name || 'Creator'
                    : transfer.seller?.name || 'Your published content';

                  return (
                    <button
                      key={`${transfer.id}-${transfer.createdAt}`}
                      type="button"
                      onClick={() => setSelectedTransferId(transfer.id)}
                      className={`grid w-full gap-3 rounded-2xl border p-4 text-left transition sm:grid-cols-[minmax(0,1fr)_220px] ${
                        isSelected
                          ? 'border-orange-300 bg-orange-50/60 shadow-sm'
                          : 'border-slate-200 bg-white hover:border-orange-200 hover:bg-orange-50/30'
                      }`}
                    >
                      <div className="min-w-0">
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5 flex h-11 w-11 items-center justify-center rounded-2xl bg-orange-100 text-orange-700">
                            <ArrowDownLeft className="h-5 w-5" />
                          </div>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-slate-900">{leadingName}</p>
                            <p className="truncate text-xs text-slate-500">
                              {isAdmin ? `Creator: ${secondaryName}` : `Published by: ${secondaryName}`}
                            </p>
                            <p className="mt-2 line-clamp-2 text-xs text-slate-600">{summarizeNote(transfer)}</p>
                            <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] font-medium text-slate-500">
                              <span className="rounded-full bg-slate-100 px-2 py-1">{transfer.transactionRef}</span>
                              <span>{formatDate(transfer.createdAt)}</span>
                              <span>{transfer.itemCount} item{transfer.itemCount === 1 ? '' : 's'}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-start justify-between gap-3 sm:flex-col sm:items-end">
                        <div className="text-right">
                          <p className="text-base font-semibold text-slate-900">
                            {formatCurrency(
                              isAdmin ? transfer.platformCommission : transfer.creatorEarnings,
                              transfer.currency,
                            )}
                          </p>
                          <p className="text-xs text-slate-500">{isAdmin ? 'Admin 30%' : 'Your 70%'}</p>
                        </div>
                        <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${statusTone[currentStatus]}`}>
                          {statusLabel[currentStatus]}
                        </span>
                        <p className="text-xs text-slate-500">
                          Total sale {formatCurrency(transfer.amount, transfer.currency)}
                        </p>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </section>
        </div>

        <aside className="space-y-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div>
            <h4 className="text-sm font-semibold text-slate-900">Transfer Details</h4>
            <p className="mt-1 text-xs text-slate-500">
              {isAdmin
                ? 'Inspect what entered the platform account and what belongs to the creator.'
                : 'Inspect the exact split for each sale and track whether your 70% has been paid out.'}
            </p>
          </div>

          {!selectedTransfer ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-sm text-slate-500">
              Select a transfer to view the full 30/70 breakdown.
            </div>
          ) : (
            <>
              {detailsLoading && (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500">
                  Loading transfer details...
                </div>
              )}

              {detailsError && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                  {detailsError}
                </div>
              )}

              <article className="rounded-2xl border border-orange-100 bg-orange-50/40 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-900">
                      {selectedTransfer.customer?.name || 'Customer'}
                    </p>
                    <p className="mt-1 truncate text-xs text-slate-500">{selectedTransfer.customer?.email || 'No buyer email available'}</p>
                  </div>
                  {selectedStatus ? (
                    <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${statusTone[selectedStatus]}`}>
                      {statusLabel[selectedStatus]}
                    </span>
                  ) : null}
                </div>

                <dl className="mt-4 grid gap-3 text-sm text-slate-700">
                  <div className="rounded-xl bg-white px-3 py-2">
                    <dt className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Reference</dt>
                    <dd className="mt-1 font-semibold text-slate-900">{selectedTransfer.transactionRef}</dd>
                  </div>
                  <div className="rounded-xl bg-white px-3 py-2">
                    <dt className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Creator</dt>
                    <dd className="mt-1 font-semibold text-slate-900">{selectedTransfer.seller?.name || 'Not assigned'}</dd>
                    <dd className="text-xs text-slate-500">{selectedTransfer.seller?.email || ''}</dd>
                  </div>
                  <div className="rounded-xl bg-white px-3 py-2">
                    <dt className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Recorded</dt>
                    <dd className="mt-1 font-semibold text-slate-900">{formatDate(selectedTransfer.createdAt)}</dd>
                  </div>
                  {!isAdmin && (
                    <div className="rounded-xl bg-white px-3 py-2">
                      <dt className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Payout state</dt>
                      <dd className="mt-1 font-semibold text-slate-900">
                        {selectedTransfer.sellerReceivedPayment
                          ? 'Paid to creator'
                          : selectedTransfer.sellerPaid
                            ? 'Marked as paid'
                            : 'Waiting for payout'}
                      </dd>
                      {selectedTransfer.sellerPaidAt ? (
                        <dd className="text-xs text-slate-500">Updated {formatDate(selectedTransfer.sellerPaidAt)}</dd>
                      ) : null}
                    </div>
                  )}
                </dl>
              </article>

              <PaymentSplitInfo
                amount={selectedTransfer.amount}
                platformCommission={selectedTransfer.platformCommission}
                creatorEarnings={selectedTransfer.creatorEarnings}
                currency={selectedTransfer.currency}
                direction="incoming"
              />

              <article className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-center gap-2">
                  <Wallet className="h-4 w-4 text-orange-600" />
                  <h5 className="text-sm font-semibold text-slate-900">Item Breakdown</h5>
                </div>
                {'items' in selectedTransfer && (selectedTransfer.items as Array<{paymentId: string; title: string; status: string; amount: number; platformCommission: number; creatorEarnings: number}>).length > 0 ? (
                  <div className="mt-3 space-y-3">
                    {(selectedTransfer.items as Array<{paymentId: string; title: string; status: string; amount: number; platformCommission: number; creatorEarnings: number}>).map((item) => {
                      const itemStatus = normalizeTransferStatus(item.status);
                      return (
                        <div key={item.paymentId} className="rounded-xl border border-slate-200 bg-white p-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="truncate text-sm font-semibold text-slate-900">{item.title}</p>
                              <p className="mt-1 text-xs text-slate-500">Payment ID: {item.paymentId}</p>
                            </div>
                            <span className={`rounded-full px-2 py-1 text-[11px] font-semibold ${statusTone[itemStatus]}`}>
                              {statusLabel[itemStatus]}
                            </span>
                          </div>
                          <div className="mt-3 grid gap-2 text-xs text-slate-600">
                            <div className="flex items-center justify-between">
                              <span>Total</span>
                              <span className="font-semibold text-slate-900">{formatCurrency(item.amount, selectedTransfer.currency)}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span>Platform 30%</span>
                              <span className="font-semibold text-orange-700">
                                {formatCurrency(item.platformCommission, selectedTransfer.currency)}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span>Creator 70%</span>
                              <span className="font-semibold text-emerald-700">
                                {formatCurrency(item.creatorEarnings, selectedTransfer.currency)}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="mt-3 rounded-xl border border-dashed border-slate-300 bg-white px-4 py-4 text-sm text-slate-500">
                    Detailed item rows are not available for this transfer yet.
                  </div>
                )}
              </article>

              <article className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex items-start gap-3">
                  {selectedStatus === 'failed' ? (
                    <XCircle className="mt-0.5 h-5 w-5 text-rose-500" />
                  ) : selectedStatus === 'completed' ? (
                    <CircleCheckBig className="mt-0.5 h-5 w-5 text-emerald-500" />
                  ) : (
                    <Clock3 className="mt-0.5 h-5 w-5 text-orange-500" />
                  )}
                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      {selectedStatus === 'completed'
                        ? isAdmin
                          ? 'Platform revenue captured'
                          : 'Creator payout completed'
                        : selectedStatus === 'failed'
                          ? 'This transfer failed'
                          : 'This transfer is still moving through the flow'}
                    </p>
                    <p className="mt-1 text-sm text-slate-500">
                      {isAdmin
                        ? `The platform share for this sale is ${formatCurrency(selectedTransfer.platformCommission, selectedTransfer.currency)}.`
                        : `Your creator share for this sale is ${formatCurrency(selectedTransfer.creatorEarnings, selectedTransfer.currency)}.`}
                    </p>
                  </div>
                </div>
              </article>
            </>
          )}
        </aside>
      </div>
    </div>
  );
};

export default TransferProcessBoard;
